// App-scope UI state: role selection. (Engage patient selection is URL-driven
// via the /engage/:withingsUserId route — no longer held here.)

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { normalizeRoleId } from '@/data/roles';

export type UserRole = 'care_coordinator' | 'clinician';

interface UiState {
  userRole: UserRole;
  setUserRole: (role: string) => void;
}

const STORAGE_KEY = 'access_user_role';

// Read-only — HMR re-evaluates this module on hot reload, so any write
// here would fire on every save. localStorage is written from setUserRole
// instead (the only path that genuinely changes the persisted value).
function initialRole(): UserRole {
  if (typeof window === 'undefined') return 'care_coordinator';
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved && saved !== 'all') return normalizeRoleId(saved) as UserRole;
  return 'care_coordinator';
}

const useUiStore = create<UiState>()(
  devtools((set) => ({
    userRole: initialRole(),
    setUserRole: (role) => {
      const normalized = normalizeRoleId(role) as UserRole;
      window.localStorage.setItem(STORAGE_KEY, normalized);
      set({ userRole: normalized });
    },
  }), { name: 'useUiStore' }),
);

export const useUserRole = () => useUiStore((s) => s.userRole);
export const useSetUserRole = () => useUiStore((s) => s.setUserRole);

export default useUiStore;
